import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PARENT_NAV } from "./ParentDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Search, ShoppingCart, Plus, Minus, Filter } from "lucide-react";

const CATS = ["All", "Academic Books", "Library Books", "Stationery", "Uniforms",
              "School Shoes", "Sports Equipment", "Laboratory Supplies", "IT Equipment"];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [subCat, setSubCat] = useState("All");
  const [q, setQ] = useState("");
  const { add, items } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const params = { school_id: user?.school_id, class_level: user?.student_class };
    if (cat !== "All") params.category = cat;
    api.get("/products", { params }).then((r) => setProducts(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [cat, user]);

  // Reset sub-category when top-level changes.
  useEffect(() => { setSubCat("All"); }, [cat]);

  const subcats = useMemo(() => {
    const s = new Set();
    products.forEach((p) => { if (p.subcategory) s.add(p.subcategory); });
    return ["All", ...Array.from(s).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return products.filter((p) => {
      if (subCat !== "All" && p.subcategory !== subCat) return false;
      if (!ql) return true;
      return (p.name || "").toLowerCase().includes(ql) ||
             (p.description || "").toLowerCase().includes(ql) ||
             (p.subcategory || "").toLowerCase().includes(ql);
    });
  }, [products, q, subCat]);

  const qtyIn = (id) => items.find((x) => x.type === "product" && x.product_id === id)?.qty || 0;

  return (
    <DashboardLayout nav={PARENT_NAV} title="School-approved Catalog">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="cat-search"
                 className="w-full pl-10 pr-4 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
        </div>
        {subcats.length > 1 && (
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            <select value={subCat} onChange={(e) => setSubCat(e.target.value)}
                    data-testid="cat-subcat"
                    className="rounded-md border border-slate-300 py-2 px-3 text-sm">
              {subcats.map((sc) => <option key={sc} value={sc}>{sc}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-btn-${c}`}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors duration-200 ${
                    cat === c ? "bg-brand-blue text-white border-brand-blue"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}>{c}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {filtered.map((p) => (
          <div key={p.id} className="sl-card overflow-hidden relative flex flex-col" data-testid={`product-${p.id}`}>
            <div className="h-44 bg-white flex items-center justify-center overflow-hidden relative border-b border-slate-100">
              <img
                src={p.image || "/api/static/library-covers/cover-unavailable.webp"}
                alt={p.name} loading="lazy"
                className={`max-h-full max-w-full object-contain ${p.is_live === false ? "opacity-60 grayscale" : ""}`}
                onError={(e) => { e.currentTarget.src = "/api/static/library-covers/cover-unavailable.webp"; }}
              />
              {p.is_live === false && (
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 bg-white/95 border border-slate-200 rounded-full px-2 py-0.5">
                  Coming Soon
                </span>
              )}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-xs text-brand-blue font-semibold">
                {p.subcategory || p.category}
              </div>
              <h3 className="font-heading font-semibold text-slate-900 mt-1 line-clamp-2 min-h-[2.75rem]">{p.name}</h3>
              {(p.gsm || p.size || p.pack_size) && (
                <div className="text-[11px] text-slate-500 mt-1 space-x-2">
                  {p.size && <span>Size: <b>{p.size}</b></span>}
                  {p.gsm && <span>GSM: <b>{p.gsm}</b></span>}
                  {p.pack_size && <span>Pack: <b>{p.pack_size}</b></span>}
                </div>
              )}
              <div className="mt-auto pt-3 flex items-center justify-between min-h-[40px]">
                {Number(p.price) > 0 ? (
                  <div className="flex flex-col">
                    {Number(p.mrp) > 0 && Number(p.mrp) > Number(p.price) && (
                      <span className="text-[11px] text-slate-400 line-through">₹{Number(p.mrp).toLocaleString('en-IN')}</span>
                    )}
                    <span className="font-bold text-lg text-brand-navy" data-testid={`price-${p.id}`}>
                      ₹{Number(p.price).toLocaleString('en-IN')}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400" data-testid={`price-tba-${p.id}`}>Price on request</span>
                )}
                {p.is_live === false ? (
                  <button data-testid={`add-${p.id}`} disabled
                          onClick={() => toast.info("Coming Soon.")}
                          className="rounded-md bg-slate-200 text-slate-500 text-sm font-semibold px-3 py-1.5 cursor-not-allowed">
                    Soon
                  </button>
                ) : qtyIn(p.id) === 0 ? (
                  <button data-testid={`add-${p.id}`}
                          onClick={() => { add(p); toast.success("Added to cart"); }}
                          disabled={!(Number(p.price) > 0)}
                          className="rounded-md bg-brand-blue text-white text-sm font-semibold px-3 py-1.5 hover:bg-brand-blueDark disabled:bg-slate-300 inline-flex items-center gap-1">
                    <ShoppingCart size={12}/> Add
                  </button>
                ) : (
                  <QtyStepper p={p} />
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="sl-card p-8 col-span-full text-slate-500 text-center">
            No products match your search.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function QtyStepper({ p }) {
  const cart = useCart();
  const idx = cart.items.findIndex((x) => x.type === "product" && x.product_id === p.id);
  const line = idx >= 0 ? cart.items[idx] : null;
  const q = line?.qty || 0;
  return (
    <div className="flex items-center border border-slate-300 rounded-md overflow-hidden">
      <button data-testid={`dec-${p.id}`}
              onClick={() => q <= 1 ? cart.removeLine(idx) : cart.setProductQty(idx, q - 1)}
              className="p-1.5 hover:bg-slate-100"><Minus size={12}/></button>
      <span data-testid={`qty-${p.id}`} className="min-w-[24px] text-center text-sm font-semibold">{q}</span>
      <button data-testid={`inc-${p.id}`}
              onClick={() => cart.setProductQty(idx, q + 1)}
              className="p-1.5 hover:bg-slate-100"><Plus size={12}/></button>
    </div>
  );
}
